import React, { useEffect, useRef, useState } from 'react';
import { Paper, Box } from '@mui/material';
import { useAtomValue } from 'jotai';

import { ChatHeader } from './ChatHeader';
import ChatInput from './ChatInput';
import { ChatMessageList } from './ChatMessageList';
import { SuggestionsBox } from './SuggestionsBox';
import { SmartStarter } from './SmartStarter';
import { chatMessagesState } from '../../state/chat/ChatMessagesState';
import { chatbotConfigState } from '../../state/config/ChatbotConfigState';
import { AWN_PRIMARY_COLOR } from '../../style/constants';
import wallpaper1 from '../../../assets/wallpaper-1.png';
import wallpaper2 from '../../../assets/wallpaper-2.png';
import wallpaper3 from '../../../assets/wallpaper-3.png';

const FALLBACK_WALLPAPER = 'none';

const CHAT_WALLPAPER_OPTIONS = [
    {
        value: 'wallpaper_1',
        assetPath: wallpaper1,
    },
    {
        value: 'wallpaper_2',
        assetPath: wallpaper2,
    },
    {
        value: 'wallpaper_3',
        assetPath: wallpaper3,
    },
    {
        value: 'none',
        assetPath: '',
        isPlain: true,
    },
];

function getWallpaperOption(value) {
    return CHAT_WALLPAPER_OPTIONS.find((item) => item.value === value) || CHAT_WALLPAPER_OPTIONS.find((item) => item.value === FALLBACK_WALLPAPER);
}

function getChatAreaBackgroundSx(chatWallpaperValue) {
    const selected = getWallpaperOption(chatWallpaperValue);

    if (!selected || selected.isPlain) {
        return {
            backgroundImage: 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.96) 100%)',
            backgroundColor: 'var(--component-bg-light)',
        };
    }

    return {
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.12) 100%), url(${selected.assetPath})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
    };
}

export function ChatWindow({ isMobile }) {
    const chatMessages = useAtomValue(chatMessagesState);
    const config = useAtomValue(chatbotConfigState);
    const boxRef = useRef(null);
    const [prevChatMessagesLength, setPrevChatMessagesLength] = useState(0);
    const [prevScrollHeight, setPrevScrollHeight] = useState(0);
    const [prevScrollTop, setPrevScrollTop] = useState(0);

    const scrollbarThumbColor = config.primaryColor || AWN_PRIMARY_COLOR || '#172983';

    function scrollToBottom() {
        if (boxRef.current) {
            boxRef.current.scrollTo({
                top: boxRef.current.scrollHeight,
                behavior: 'auto',
            });
        }
    }

    useEffect(() => {
        if (boxRef.current) {
            if (
                chatMessages.length - prevChatMessagesLength === 1 &&
                (chatMessages.slice().pop()?.media?.length ?? 0) === 0
            ) {
                scrollToBottom();
            }
            setPrevChatMessagesLength(chatMessages.length);
            setPrevScrollHeight(boxRef.current.scrollHeight);
            setPrevScrollTop(boxRef.current.scrollTop);
        }
    }, [chatMessages, prevChatMessagesLength]);

    useEffect(() => {
        if (boxRef.current) {
            const newScrollHeight = boxRef.current.scrollHeight;
            const scrollDiff = newScrollHeight - prevScrollHeight;
            if (Math.abs(prevScrollTop + boxRef.current.clientHeight - prevScrollHeight) > 5) {
                boxRef.current.scrollTop = prevScrollTop + scrollDiff;
            } else {
                boxRef.current.scrollTop = boxRef.current.scrollHeight;
            }
        }
    }, [prevScrollHeight, prevScrollTop]);

    return (
        <Paper
            elevation={0}
            sx={{
                flex: isMobile ? 1 : '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                height: '100%',
                maxHeight: '100%',
                mx: 'auto',
                alignSelf: 'stretch',
                borderRadius: isMobile ? 0 : 6,
                overflow: 'hidden',
                backgroundColor: 'var(--component-bg)',
                backdropFilter: 'blur(20px)',
                border: '1px solid var(--border-color)',
                boxShadow: '0 24px 52px rgba(15, 23, 42, 0.17)',
            }}
        >
            <ChatHeader isMobile={isMobile} />

            <Box
                ref={boxRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 1.1, sm: 1.35 },
                    ...getChatAreaBackgroundSx(config.chatWallpaper),
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${scrollbarThumbColor} var(--border-color-light)`,
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'var(--border-color-light)',
                        borderRadius: '999px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: scrollbarThumbColor,
                        borderRadius: '999px',
                    },
                }}
            >
                <ChatMessageList />

                <Box
                    sx={{
                        pt: 0.4,
                        color: '#1f2937',
                        '& .MuiTypography-root': {
                            color: '#1f2937',
                        },
                        '& .MuiButtonBase-root, & button, & [role="button"]': {
                            color: '#1f2937',
                        },
                        '& .MuiSvgIcon-root': {
                            color: 'rgba(30, 41, 59, 0.76)',
                        },
                        '& .MuiButton-outlined, & .MuiChip-root, & .MuiPaper-root': {
                            borderColor: 'rgba(100, 116, 139, 0.28)',
                        },
                        '& .MuiButton-outlined, & .MuiChip-root': {
                            backgroundColor: 'rgba(255,255,255,0.78)',
                            backdropFilter: 'blur(6px)',
                        },
                        '& .MuiButton-outlined:hover, & .MuiChip-root:hover': {
                            backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                    }}
                >
                    <SmartStarter />
                    <SuggestionsBox />
                </Box>
            </Box>

            <Box
                sx={{
                    borderTop: '1px solid var(--border-color)',
                    backgroundColor: 'var(--component-bg)',
                }}
            >
                <ChatInput />
            </Box>
        </Paper>
    );
}
